import React from 'react';
import {ViewStyle, FlatList, View, StyleSheet} from 'react-native';
import colors from 'theme/Colors';
import {corner_radius, spacing} from 'theme/Units';
import {Pagination, Text} from 'ui-kits';

type Column = {
  key: string;
  label: JSX.Element | string;
  minWidth?: number|string;
  maxWidth?: number|string;
  renderCell?: (cellData: any) => JSX.Element | null;
};

type TableProps = {
  columns: Column[];
  data: any[];
  currentPage: number;
  totalPages: number;
  pageHandler: (pageNumber: number) => void;
  keyExtractor: (item: any) => string;
  pagination?: boolean;
};

export const Table: React.FC<TableProps> = ({
  columns,
  data,
  pageHandler,
  currentPage,
  totalPages,
  keyExtractor,
  pagination = true,
}) => {
  const defaultMinWidth: number = 100;
  const defaultMaxWidth: number = 300;

  return (
    <View style={styles.mainTableContainer}>
      {/* table */}
      <View style={styles.table}>
        {/* Table Header */}
        <View style={styles.headerRow}>
          {columns.map(col => {
            const cellWidth: ViewStyle = {
              minWidth: col?.minWidth || defaultMinWidth,
              maxWidth: col?.maxWidth || defaultMaxWidth,
            };
            return (
              <View key={col.key} style={[styles.cellView, cellWidth]}>
                {typeof col.label === 'string' ? (
                  <Text style={styles.headerCell}>{col.label}</Text>
                ) : (
                  col.label
                )}
              </View>
            );
          })}
        </View>

        {/* Table Rows */}
        <FlatList
          id="scroll-none"
          data={data}
          keyExtractor={keyExtractor}
          renderItem={({item}) => (
            <View style={styles.row}>
              {columns.map(col => {
                const cellWidth: ViewStyle = {
                  minWidth: col?.minWidth || defaultMinWidth,
                  maxWidth: col?.maxWidth || defaultMaxWidth,
                };
                return (
                  <View key={col.key} style={[styles.cell, cellWidth]}>
                    {col.renderCell ? (
                      col.renderCell(item)
                    ) : (
                      <Text>{item[col.key]}</Text>
                    )}
                  </View>
                );
              })}
            </View>
          )}
        />
      </View>

      {pagination ? (
        <>
          {/* bottom text */}
          {totalPages !== 1 && (
            <Text style={styles.pageNumber}>
              Showing {data.length} of {totalPages * 10} results
            </Text>
          )}

          {/* pagination buttons */}
          {totalPages !== 1 && (
            <Pagination
              currentPage={currentPage}
              pageHandler={pageHandler}
              totalPages={totalPages}
            />
          )}
        </>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  mainTableContainer: {
    flexDirection: 'column',
    marginVertical: spacing['4'],
    flex: 1,
  },
  pageNumber: {
    fontSize: 14,
    marginTop: spacing['2'],
    zIndex: -1,
  },
  table: {
    borderWidth: 1,
    flex: 1,
    borderColor: colors.palette.stroke,
    borderRadius: corner_radius['rounded-md'],
    maxHeight: 500,
  },
  headerRow: {
    backgroundColor: colors.palette.brandBlue10,
    maxHeight: 56,
    flexDirection: 'row',
    // justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    zIndex: 10,
  },
  cellView: {
    flex: 1,
    padding: 12,
  },
  headerCell: {
    fontWeight: 500,
    fontSize: 18,
  },
  row: {
    flexDirection: 'row',
    // justifyContent: 'space-between',
    alignItems: 'center',
    height: 56,
    borderBottomWidth: 1,
    borderBottomColor: colors.palette.stroke,
    paddingVertical: spacing['3'],
    paddingHorizontal: spacing['4'],
    zIndex: -10,
  },
  cell: {
    flex: 1,
    padding: 12,
    fontSize: 18,
  },
});
